/**
 * บันทึกลงเครื่องก่อนเสมอ แล้วค่อยส่งขึ้นเซิร์ฟเวอร์
 *
 * ★★ **นี่คือจุดที่ถ้าพัง เด็กจะเลิกใช้ทันทีและไม่กลับมาอีก**
 *
 * Safari บน iPad ฆ่าแท็บเบื้องหลังโดยไม่เตือนและไม่มี event ให้ดักตอนถูกฆ่า
 * (`beforeunload` ไม่ยิงบน iOS) สิ่งเดียวที่เชื่อถือได้คือ `visibilitychange`
 * ตอนกลายเป็น `hidden` — ซึ่งยิงตอนกดปุ่มโฮม สลับแอป ล็อกจอ และรับสาย
 *
 * กลยุทธ์:
 *   1. **ทุกครั้งที่ปล่อยปากกา / ตอบข้อ** → เขียน IndexedDB ทันที (ไม่รอ debounce)
 *   2. ยิงเซิร์ฟเวอร์แบบหน่วง 1.2 วินาที (กันยิงถี่ตอนวาดรัว)
 *   3. `visibilitychange` → hidden → **ยิงทันทีทุกอย่างที่ค้าง** ไม่รอ debounce
 *
 * ⚠️ ข้อ 1 ห้าม debounce — งานที่เขียนไปครึ่งหน้าแล้วหายเพราะรอ 300 ms
 * คือความเสียหายที่เรียกคืนไม่ได้ ส่วนการเขียน IndexedDB ถี่ ๆ ราคาถูกมาก
 *
 * ⚠️ PDPA — ไม่ log ชื่อเล่นเด็กหรือคำตอบรายข้อ ใช้ `learner_id` อย่างเดียว
 */
import { del, get, keys, set } from 'idb-keyval'

import type { Stroke } from '@/features/pad/strokes'

const ANSWER_PREFIX = 'ans:'
const STROKE_PREFIX = 'pad:'
const ATTEMPT_PREFIX = 'att:'

export interface DraftAnswer {
  attemptId: string
  questionId: string
  chosenChoiceId: string | null
  flagged: boolean
  timeSpentMs: number
  /** เวลาที่บันทึกลงเครื่อง — ใช้ตัดสินว่าค่าไหนใหม่กว่าเมื่อ sync ชนกัน */
  savedAt: number
  /** ยังไม่ได้ยืนยันจากเซิร์ฟเวอร์ */
  pending: boolean
}

export interface DraftAttempt {
  attemptId: string
  quizId: string
  index: number
  savedAt: number
}

const answerKey = (attemptId: string, questionId: string): string =>
  `${ANSWER_PREFIX}${attemptId}:${questionId}`
const strokeKey = (attemptId: string, questionId: string): string =>
  `${STROKE_PREFIX}${attemptId}:${questionId}`

// ─── คำตอบ ─────────────────────────────────────────────────────
export async function saveAnswerLocal(draft: DraftAnswer): Promise<void> {
  await set(answerKey(draft.attemptId, draft.questionId), draft)
}

export async function loadAnswerLocal(
  attemptId: string,
  questionId: string,
): Promise<DraftAnswer | undefined> {
  return get<DraftAnswer>(answerKey(attemptId, questionId))
}

/** คำตอบทั้งหมดของ attempt นี้ที่เก็บไว้ในเครื่อง — ทั้งที่ยิงขึ้นไปแล้วและยังไม่ได้ยิง */
export async function savedAnswers(attemptId: string): Promise<DraftAnswer[]> {
  const all = await keys()
  const mine = all.filter(
    (k) => typeof k === 'string' && k.startsWith(`${ANSWER_PREFIX}${attemptId}:`),
  )
  const drafts = await Promise.all(mine.map((k) => get<DraftAnswer>(k as string)))
  return drafts.filter((d): d is DraftAnswer => Boolean(d))
}


export async function pendingAnswers(attemptId: string): Promise<DraftAnswer[]> {
  const all = await keys()
  const mine = all.filter(
    (k) => typeof k === 'string' && k.startsWith(`${ANSWER_PREFIX}${attemptId}:`),
  )
  const drafts = await Promise.all(mine.map((k) => get<DraftAnswer>(k as string)))
  return drafts.filter((d): d is DraftAnswer => Boolean(d?.pending))
}

export async function markAnswerSynced(attemptId: string, questionId: string): Promise<void> {
  const key = answerKey(attemptId, questionId)
  const draft = await get<DraftAnswer>(key)
  if (draft) await set(key, { ...draft, pending: false })
}

// ─── รอยเขียน ───────────────────────────────────────────────────
export interface DraftStrokes {
  attemptId: string
  questionId: string
  strokes: Stroke[]
  savedAt: number
  pending: boolean
}

export async function saveStrokesLocal(draft: DraftStrokes): Promise<void> {
  await set(strokeKey(draft.attemptId, draft.questionId), draft)
}

export async function loadStrokesLocal(
  attemptId: string,
  questionId: string,
): Promise<Stroke[]> {
  const draft = await get<DraftStrokes>(strokeKey(attemptId, questionId))
  return draft?.strokes ?? []
}

export async function pendingStrokes(attemptId: string): Promise<DraftStrokes[]> {
  const all = await keys()
  const mine = all.filter(
    (k) => typeof k === 'string' && k.startsWith(`${STROKE_PREFIX}${attemptId}:`),
  )
  const drafts = await Promise.all(mine.map((k) => get<DraftStrokes>(k as string)))
  return drafts.filter((d): d is DraftStrokes => Boolean(d?.pending))
}

export async function markStrokesSynced(attemptId: string, questionId: string): Promise<void> {
  const key = strokeKey(attemptId, questionId)
  const draft = await get<DraftStrokes>(key)
  if (draft) await set(key, { ...draft, pending: false })
}

// ─── ตำแหน่งที่ทำค้างไว้ ────────────────────────────────────────
/**
 * จำว่าเด็กทำถึงข้อไหน — **ต้องกลับมาที่ข้อเดิม ไม่ใช่ข้อแรก**
 * ถูกเรียกไปกินข้าวกลางคันแล้วกลับมาเจอข้อ 1 คือเหตุผลที่เด็กจะเลิกใช้
 */
export async function saveAttemptCursor(draft: DraftAttempt): Promise<void> {
  await set(`${ATTEMPT_PREFIX}${draft.attemptId}`, draft)
}

export async function loadAttemptCursor(attemptId: string): Promise<DraftAttempt | undefined> {
  return get<DraftAttempt>(`${ATTEMPT_PREFIX}${attemptId}`)
}

export async function clearAttemptLocal(attemptId: string): Promise<void> {
  const all = await keys()
  const mine = all.filter(
    (k) =>
      typeof k === 'string' &&
      (k.startsWith(`${ANSWER_PREFIX}${attemptId}:`) ||
        k.startsWith(`${STROKE_PREFIX}${attemptId}:`) ||
        k === `${ATTEMPT_PREFIX}${attemptId}`),
  )
  await Promise.all(mine.map((k) => del(k as string)))
}

// ─── ตัวยิงขึ้นเซิร์ฟเวอร์ ───────────────────────────────────────
export type FlushFn = () => Promise<void>

/**
 * ตัวช่วยหน่วงการยิงเซิร์ฟเวอร์ + บังคับยิงทันทีเมื่อแท็บกำลังจะหายไป
 *
 * ⚠️ ใช้ `visibilitychange` เป็นหลัก **ไม่ใช่ `beforeunload`** —
 * บน iOS Safari `beforeunload` ไม่ยิงเลยเมื่อผู้ใช้กดโฮมหรือสลับแอป
 * ส่วน `pagehide` ยิงบ้างไม่ยิงบ้าง จึงดักทั้งสองตัวเป็นตาข่ายซ้อน
 */
export function createFlusher(flush: FlushFn, delayMs = 1200): {
  schedule: () => void
  flushNow: () => void
  dispose: () => void
} {
  let timer: number | undefined
  let running = false

  const run = (): void => {
    if (running) return
    running = true
    void flush().finally(() => {
      running = false
    })
  }

  const schedule = (): void => {
    window.clearTimeout(timer)
    timer = window.setTimeout(run, delayMs)
  }

  const flushNow = (): void => {
    window.clearTimeout(timer)
    run()
  }

  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') flushNow()
  }
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('pagehide', flushNow)

  return {
    schedule,
    flushNow,
    dispose(): void {
      window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', flushNow)
    },
  }
}
